/**
 * TOSS Web Application - Real-time Notifications Hook
 *
 * React hook for real-time notification updates via SignalR.
 * Features:
 * - Automatic connection management based on auth state
 * - Real-time notification reception
 * - Connection state tracking
 * - Integration with TanStack Query cache
 *
 * Usage:
 * - Call useRealtimeNotifications() in a top-level component
 * - It will automatically connect when user is authenticated
 * - It will automatically disconnect when user logs out
 */

import { useEffect, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { signalRManager } from './signalr';
import { addNotificationToCache, invalidateNotifications } from '@/hooks/api/useNotifications';
import {
  NotificationHubMethods,
  type Notification,
  type NotificationEvent,
  type ApplicationUpdateEvent,
  type TaskUpdateEvent,
  type ExpenseUpdateEvent,
  type SignalRConnectionState,
} from '@/types';
import { useToast } from '@/store/uiStore';

// ============================================================================
// Types
// ============================================================================

/**
 * Options for useRealtimeNotifications hook
 */
export interface UseRealtimeNotificationsOptions {
  /** Enable real-time notifications (default: true) */
  enabled?: boolean;
  /** Callback when new notification is received */
  onNotification?: (notification: Notification) => void;
  /** Callback when application status changes */
  onApplicationUpdate?: (event: ApplicationUpdateEvent) => void;
  /** Callback when task status changes */
  onTaskUpdate?: (event: TaskUpdateEvent) => void;
  /** Callback when expense status changes */
  onExpenseUpdate?: (event: ExpenseUpdateEvent) => void;
  /** Callback when connection state changes */
  onConnectionStateChange?: (state: SignalRConnectionState) => void;
  /** Callback when forced logout is received (user blocked) */
  onForceLogout?: (reason: string) => void;
}

/**
 * Return type for useRealtimeNotifications hook
 */
export interface UseRealtimeNotificationsResult {
  /** Current connection state */
  connectionState: SignalRConnectionState;
  /** Whether connected to the hub */
  isConnected: boolean;
  /** Manually reconnect */
  reconnect: () => Promise<void>;
  /** Manually disconnect */
  disconnect: () => Promise<void>;
}

// ============================================================================
// Force Logout Messaging
// ============================================================================

/**
 * Maps a `ForceLogout` reason to the toast shown to the disconnected user.
 *
 * This provider is the ONLY one for both the internal app and the client portal — there is
 * no portal-specific fork — so every reason the backend can send here has to read true for
 * either audience. `CREDENTIALS_CHANGED` (a customer-portal account's email/login was edited
 * from the LK «Пользователи» screen) is deliberately NOT worded like a block: the account is
 * in good standing and simply needs to sign in again with its new address, so reusing
 * `USER_BLOCKED`'s text here would tell that person they were blocked when they were not.
 *
 * `ACCESS_CHANGED` carries the same care one step further: an administrator changed the
 * account's role or its ПТО, so the access token's `role` / `ptoId` claims went stale and the
 * session is ended (`UsersService.EndSessionsIfTokenIdentityChangedAsync`). Nothing is wrong
 * with the account and its login has not moved — the person signs straight back in with the
 * same credentials and finds a different set of screens.
 *
 * The default is deliberately NEUTRAL and no longer falls through to `USER_BLOCKED`'s text.
 * A reason this build does not know about is, by definition, a reason we cannot describe —
 * and the one guess we must never make is «вас заблокировали», which is a false accusation
 * that sends the person to an administrator over what may be a routine reassignment. That
 * fall-through is exactly what a new backend code would have hit before this build shipped.
 */
function getForceLogoutMessage(reason: string): { title: string; message: string } {
  const title = 'Сессия завершена';

  switch (reason) {
    // Two spellings on purpose. `USER_DELETED` is ErrorCodes.UserDeleted; `ACCOUNT_DELETED` is a
    // bare literal shipped by AccountDeletionRequestsService when a self-deletion request is
    // approved. It stays un-renamed because the Flutter app is being taught to parse it (handoff
    // п. 71, answered 10.09.2026) — checked in the app's source that day, the literal appeared
    // nowhere in it, so the earlier «the Flutter app parses it» here was wrong about the reason,
    // not about the conclusion. Both spellings mean there is no account left to sign into — the
    // neutral default («Войдите заново») would send that person to a login that cannot succeed.
    case 'USER_DELETED':
    case 'ACCOUNT_DELETED':
      return { title, message: 'Учётная запись удалена.' };
    case 'CREDENTIALS_CHANGED':
      return { title, message: 'Администратор изменил ваш email. Войдите заново с новым адресом.' };
    case 'ACCESS_CHANGED':
      return {
        title,
        message: 'Администратор изменил ваши права доступа. Войдите заново, чтобы они вступили в силу.',
      };
    case 'USER_BLOCKED':
      return {
        title,
        message: 'Ваш аккаунт был заблокирован администратором. Обратитесь к администратору для получения информации.',
      };
    default:
      return { title, message: 'Войдите в систему заново.' };
  }
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Hook for real-time notification updates via SignalR
 *
 * @param options - Hook options
 * @returns Connection state and control methods
 *
 * @example
 * ```tsx
 * function App() {
 *   const { connectionState, isConnected } = useRealtimeNotifications({
 *     onNotification: (notification) => {
 *       toast.info(notification.title);
 *     },
 *     onApplicationUpdate: (event) => {
 *       // Refresh application data
 *       queryClient.invalidateQueries({ queryKey: ['applications', event.applicationId] });
 *     }
 *   });
 *
 *   return (
 *     <div>
 *       <ConnectionIndicator state={connectionState} />
 *       <Routes />
 *     </div>
 *   );
 * }
 * ```
 */
export function useRealtimeNotifications(
  options: UseRealtimeNotificationsOptions = {}
): UseRealtimeNotificationsResult {
  const {
    enabled = true,
    onNotification,
    onApplicationUpdate,
    onTaskUpdate,
    onExpenseUpdate,
    onConnectionStateChange,
    onForceLogout,
  } = options;

  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const logout = useAuthStore((state) => state.logout);
  const { error: showError } = useToast();

  // Track connection state
  const [connectionState, setConnectionState] = useState<SignalRConnectionState>(() =>
    signalRManager.state
  );

  /**
   * Handle connection state changes
   */
  const handleConnectionStateChange = useCallback(
    (state: SignalRConnectionState) => {
      setConnectionState(state);
      onConnectionStateChange?.(state);

      // If reconnected, invalidate notifications to refresh data
      if (state === 'Connected') {
        invalidateNotifications(queryClient);
      }
    },
    [onConnectionStateChange, queryClient]
  );

  /**
   * Handle new notification from SignalR.
   *
   * The server sends the bare `NotificationDto` (`INotificationClient.ReceiveNotification`), not
   * the `{ eventType, notification }` envelope this handler was first written for — so until
   * 24.09.2026 the `eventType === 'created'` check never matched: no live row, no toast, and the
   * bell moved only on the 30 s poll. Both shapes are accepted; `ReceiveNotification` is only ever
   * sent for a new row.
   */
  const handleNotification = useCallback(
    (payload: NotificationEvent | Notification) => {
      const notification = 'notification' in payload ? payload.notification : payload;
      if ('eventType' in payload && payload.eventType !== 'created') return;
      if (!notification?.id) return;

      addNotificationToCache(queryClient, notification);
      onNotification?.(notification);
    },
    [queryClient, onNotification]
  );

  /**
   * Handle application update from SignalR
   */
  const handleApplicationUpdate = useCallback(
    (event: ApplicationUpdateEvent) => {
      onApplicationUpdate?.(event);
    },
    [onApplicationUpdate]
  );

  /**
   * Handle task update from SignalR
   */
  const handleTaskUpdate = useCallback(
    (event: TaskUpdateEvent) => {
      onTaskUpdate?.(event);
    },
    [onTaskUpdate]
  );

  /**
   * Handle expense update from SignalR
   */
  const handleExpenseUpdate = useCallback(
    (event: ExpenseUpdateEvent) => {
      onExpenseUpdate?.(event);
    },
    [onExpenseUpdate]
  );

  /**
   * Handle force logout from SignalR. Reason-dependent message — see {@link getForceLogoutMessage}:
   * block, delete, and (client portal) an administrator changing the account's email/login are
   * three different events and must not all read as "you were blocked".
   */
  const handleForceLogout = useCallback(
    (reason: string) => {
      if (import.meta.env.DEV) {
        console.log('[Notifications] Force logout received:', reason);
      }

      // Show a reason-appropriate error message to the user
      const { title, message } = getForceLogoutMessage(reason);
      showError(title, message);

      // Call custom callback if provided
      onForceLogout?.(reason);

      // Clear all cached queries
      queryClient.clear();

      // Logout the user
      logout();
    },
    [logout, queryClient, showError, onForceLogout]
  );

  /**
   * Connect to SignalR hub
   * Note: User group membership is automatically managed by the server
   * in OnConnectedAsync/OnDisconnectedAsync based on Context.UserIdentifier
   */
  const connect = useCallback(async () => {
    if (!isAuthenticated || !enabled) {
      return;
    }

    try {
      await signalRManager.startConnection();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('[Notifications] Connection failed:', error);
      }
    }
  }, [isAuthenticated, enabled]);

  /**
   * Disconnect from SignalR hub
   */
  const disconnect = useCallback(async () => {
    await signalRManager.stopConnection();
  }, []);

  /**
   * Reconnect to SignalR hub
   */
  const reconnect = useCallback(async () => {
    await disconnect();
    await connect();
  }, [disconnect, connect]);

  // Setup connection state listener
  useEffect(() => {
    const unsubscribe = signalRManager.onStateChange(handleConnectionStateChange);
    return unsubscribe;
  }, [handleConnectionStateChange]);

  // Setup event listeners
  useEffect(() => {
    if (!signalRManager.isConnected) {
      return;
    }

    // Subscribe to events
    const unsubscribeNotification = signalRManager.on<NotificationEvent | Notification>(
      NotificationHubMethods.ReceiveNotification,
      handleNotification
    );

    const unsubscribeApplication = signalRManager.on<ApplicationUpdateEvent>(
      NotificationHubMethods.ReceiveApplicationUpdate,
      handleApplicationUpdate
    );

    const unsubscribeTask = signalRManager.on<TaskUpdateEvent>(
      NotificationHubMethods.ReceiveTaskUpdate,
      handleTaskUpdate
    );

    const unsubscribeExpense = signalRManager.on<ExpenseUpdateEvent>(
      NotificationHubMethods.ReceiveExpenseUpdate,
      handleExpenseUpdate
    );

    const unsubscribeForceLogout = signalRManager.on<string>(
      NotificationHubMethods.ForceLogout,
      handleForceLogout
    );

    // Cleanup
    return () => {
      unsubscribeNotification();
      unsubscribeApplication();
      unsubscribeTask();
      unsubscribeExpense();
      unsubscribeForceLogout();
    };
  }, [
    connectionState,
    handleNotification,
    handleApplicationUpdate,
    handleTaskUpdate,
    handleExpenseUpdate,
    handleForceLogout,
  ]);

  // Connect when authenticated, disconnect when logged out
  useEffect(() => {
    if (isAuthenticated && enabled) {
      // Start connection - this increments the generation
      signalRManager.startConnection();
    }

    // Capture current generation at effect setup time
    // This allows us to detect stale cleanup in React StrictMode
    const cleanupGeneration = signalRManager.generation;

    return () => {
      // Pass generation to stopConnection - it will ignore the request
      // if a newer connection has started (generation mismatch)
      // This prevents StrictMode's old cleanup from killing new connections
      signalRManager.stopConnection(cleanupGeneration);
    };
  }, [isAuthenticated, enabled]);

  return {
    connectionState,
    isConnected: connectionState === 'Connected',
    reconnect,
    disconnect,
  };
}

// ============================================================================
// Connection State Hook (Read-Only)
// ============================================================================

/**
 * Hook for reading SignalR connection state without managing the connection lifecycle.
 *
 * Use this hook in components that need to display connection status but should not
 * participate in connect/disconnect lifecycle management.
 *
 * For the main connection management, use useRealtimeNotifications in a top-level component.
 *
 * @example
 * ```tsx
 * function ConnectionStatus() {
 *   const { connectionState, isConnected } = useSignalRState();
 *   return <div>Status: {connectionState}</div>;
 * }
 * ```
 */
export function useSignalRState(): Pick<UseRealtimeNotificationsResult, 'connectionState' | 'isConnected'> {
  const [connectionState, setConnectionState] = useState<SignalRConnectionState>(() =>
    signalRManager.state
  );

  useEffect(() => {
    const unsubscribe = signalRManager.onStateChange(setConnectionState);
    return unsubscribe;
  }, []);

  return {
    connectionState,
    isConnected: connectionState === 'Connected',
  };
}

// ============================================================================
// Connection Indicator Component Props
// ============================================================================

/**
 * Props for connection indicator component
 */
export interface ConnectionIndicatorProps {
  /** Current connection state */
  state: SignalRConnectionState;
  /** Show indicator only when not connected */
  hideWhenConnected?: boolean;
}

// ============================================================================
// Export
// ============================================================================

export default useRealtimeNotifications;
